import React, { useState } from 'react';
import {
  FDStack,
  FDTable,
  FDTableBody,
  FDTableHead,
  FDTableRow,
  FDPrimaryActionButton,
  FDBox,
  FDEmptyState,
  FDDialogTitle,
  FDDialogContent,
  FDDeleteButton,
  FDDialogActions,
  FDTextButton,
} from '@facet-wealth/design';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import PropTypes from 'prop-types';
import {
  QuestionnaireTableCell,
  QuestionnaireTableCellNumber,
} from 'Components/Universal/Questionnaire/UI/QuestionnaireTable/TableCell';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { getDynamicDateTypeValue } from 'Components/Universal/Questionnaire/utils';
import MetaLayoutRemainingFields from 'Components/UI/Meta/MetaForm/Components/MetaLayout/MetaLayoutRemainingFields';
import { ExpenseDialog } from './ExpenseDialog';

// We want these fields to be hidden from the table but still be available in the create/edit modal.
// This honestly isn't the ideal way of handling hiding these fields, but we're just doing it
// this way to avoid scope creep for now.
const hiddenFields = [
  'isEssential',
  'startType',
  'endType',
  'startAtAge',
  'endAtAge',
  'birthDate',
  'isTaxFree',
  'realAssetID',
];

const dynamicDateTypeFields = [
  'startDate',
  'endDate',
];

const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride(hiddenFields, { hidden: true }, overrideReadOnly)
  .overrideAll({ readonly: true })
  .override({ readonly: true }, { _component: () => QuestionnaireTableCell }, overrideReadOnly)
  .override({ readonly: true, type: 'number' }, { _component: () => QuestionnaireTableCellNumber }, overrideReadOnly);

function ExpenseDialogLayout({ onCancel, isEdit }) {
  const { onDelete, onSubmit } = useMetaLayoutContext();
  return (
    <>
      <FDDialogTitle value={isEdit ? 'Edit Expense' : 'Add Expense'} />
      <FDDialogContent>
        <FDBox minWidth={500}>
          <FDStack spacing={2} direction={'column'} alignItems={'stretch'}>
            <MetaControlField attr={'name'} />
            <MetaControlField attr={'value'} />
            <MetaControlField attr={'frequency'} />
            <MetaControlField attr={'expenseGroupName'} />
            <MetaControlField attr={'expenseCategoryName'} />
            <MetaControlField attr={'isEssential'} />
            <MetaControlField attr={'realAssetID'} />
            <MetaLayoutRemainingFields expanded />
            <MetaControlField attr={'startType'} />
            <MetaControlField attr={'startDate'} />
            <MetaControlField attr={'startAtAge'} />
            <MetaControlField attr={'endType'} />
            <MetaControlField attr={'endDate'} />
            <MetaControlField attr={'endAtAge'} />
            <FDStack alignSelf={'center'}>
              {isEdit && (
                <FDDeleteButton
                  onClick={onDelete}
                />
              )}
            </FDStack>
          </FDStack>
        </FDBox>
      </FDDialogContent>
      <FDDialogActions>
        <FDPrimaryActionButton
          onClick={onSubmit}
          label="Submit"
        />
        <FDTextButton
          onClick={onCancel}
          label="Cancel"
        />
      </FDDialogActions>
    </>
  );
}

const ExpenseRedesign = ({
  collection,
  refreshCollection,
}) => {
  const [expense, setExpense] = useState(null);
  const resourceList = collection.resources().list();

  const emptyRow = (
    <FDBox spacing={1} alignSelf={'center'}>
      <FDEmptyState icon={'search'} title={'No results'} description={'No expenses found for the selected household'} />
    </FDBox>);

  return (
    <>
      <FDStack direction="column" spacing={2} alignItems="flex-end">
        <FDPrimaryActionButton
          label="Add Expense"
          icon="add"
          onClick={() => setExpense(collection.create())}
        />
        <FDTable>
          <FDTableHead>
            <FDTableRow>
              {// This isn't ideal, but we would need to add a new endpoint for this list view and that doesn't feel worth
                  // it for this questionnaire at this point so we're filtering the fields we want to hide from the table here.
                  collection.meta().template().list().filter(field => !(field.attr('hidden') || hiddenFields.includes(field.attr('name'))))
                    .map((field) => {
                      return (<QuestionnaireTableCell key={field.attr('name')} value={field.attr('prompt')} hidden={field.attr('hidden')} />);
                    })}
            </FDTableRow>
          </FDTableHead>
          <FDTableBody>
            {resourceList.map((resource) => {
              // This is a hack to get the start and end date values to display in the table.
              // These values for these fields are driven by excludedUnless logic, but that isn't supported here
              const { key: startKey, value: startValue } = getDynamicDateTypeValue(resource, 'start');
              const { key: endKey, value: endValue } = getDynamicDateTypeValue(resource, 'end');

              return (
                <FDTableRow key={`${resource.id()}`} onClick={() => setExpense(resource.copy())} hover>
                  {resource.meta().template().list()
                    .filter(field => !dynamicDateTypeFields.includes(field.attr('name')))
                    .map((metaField) => (
                      <MetaControl key={metaField.attr('name')} resource={resource} attrName={metaField.attr('name')} metaOverrideSet={metaOverrideSet} />
                    ))}
                  <QuestionnaireTableCell key={startKey} value={startValue} />
                  <QuestionnaireTableCell key={endKey} value={endValue} />
                </FDTableRow>
              );
            })}
          </FDTableBody>
        </FDTable>
        {collection.resources().empty() ? emptyRow : null}
        {expense
          ? <ExpenseDialog
            metaLayout={ExpenseDialogLayout}
            resource={expense}
            onCancel={() => setExpense(null)}
            onSubmit={() => {
              setExpense(null);
              refreshCollection();
            }}
            onDelete={() => {
              setExpense(null);
              refreshCollection();
            }}
          />
          : null}
      </FDStack>
    </>
  );
};

ExpenseRedesign.defaultProps = {
};

ExpenseRedesign.propTypes = {
  collection: PropTypes.object.isRequired,
  refreshCollection: PropTypes.func.isRequired,
};

export default ExpenseRedesign;
